import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { browserHelpers } from '@core';
import { type DeviceData } from '@devices';
import { SpaceType } from '@locations';
// Selectors are imported from locations library to avoid circular dependency between devices and locations libraries.
import { LocationSelectorComponent, SpaceSelectorComponent } from '@locations/components/selectors/';
import { MessageComponent, NotificationsService } from '@ui';

import { BtService, InventoryService } from '../../services';
import { WifiPickerComponent } from '../wifi';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatIcon,
    MatInputModule,
    LocationSelectorComponent,
    SpaceSelectorComponent,
    MessageComponent,
    NgTemplateOutlet,
    WifiPickerComponent,
  ],
  providers: [BtService],
  selector: 'avs-register-device-dialog',
  templateUrl: './register-device-dialog.component.html',
})
export class RegisterDeviceDialogComponent {
  protected browserHelpers = browserHelpers;
  protected building = signal('');
  protected registeredDevice = signal<DeviceData | null>(null);
  protected eSpaceType = SpaceType;
  protected floor = signal('');
  protected room = signal('');
  protected serialNumber = signal('');
  protected showWifiSection = signal(false);
  protected wifiPicker = viewChild.required(WifiPickerComponent);

  protected isValid = computed(() =>
    this.building() &&
    this.floor() &&
    this.room() &&
    this.serialNumber(),
  );

  private btService = inject(BtService);
  private dialogRef = inject(MatDialogRef, { optional: true });
  private inventoryService = inject(InventoryService);
  private notification = inject(NotificationsService);

  protected async connect() {
    await this.wifiPicker().connect();
    this.dialogRef?.close(true);
  }

  protected async pair() {
    await this.btService.pair();
    this.serialNumber.set(await this.btService.getSerialNumber());
  }

  protected async register() {
    const isSuccessful = await this.registerDevice();

    if (isSuccessful) {
      this.dialogRef?.close(true);
    }
  }

  protected async registerAndConnect() {
    const isSuccessful = await this.registerDevice();

    if (isSuccessful) {
      this.showWifiSection.set(true);
    }
  }

  protected async registerDevice() {
    try {
      const device = await this.inventoryService.registerDevice({
        locationId: this.building(),
        serialNumber: this.serialNumber(),
        spaceId: this.room(),
      });

      this.registeredDevice.set(device);
      this.notification.showSuccessMessage(`${device.friendlyName} registered successfully`);

      return true;
    } catch (error) {
      this.notification.showErrorMessage(`Failed to register device: ${(error as Error).message || String(error)}`);

      return false;
    }
  }
}
